import * as React from 'react';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Row } from '@tanstack/react-table';
import type { Trip } from 'contracts/tracking-v2/trips';
import { TRACKING_V2_ROUTES, SCHEDULED_REPORTS_ROUTES } from 'lib/routes';
import { notify } from 'services/notifications';
import { useFetchPaginatedTrips } from 'hooks/api/trips';
import { useCreateScheduledReport } from 'hooks/api/scheduled-reports';
import {
  useTripsTrackingSearchParams,
} from 'hooks/store/trips/use-trips-tracking-search-params';
import { Scaffold } from 'layouts';
import { PageHeaderForFilters } from 'components/ui/page-header-for-filters';
import { DataTableV2, type SortingProps } from 'components/ui/DataTableV2';
import {
  TabbedNavigation,
  ClearFiltersButton,
} from 'pages/tracking-v2/shared-components';

import { useTripColumns } from './hooks';
import {
  PageTitle,
  TripFilters,
  type TripFiltersRef,
  RefineReportParamsDrawer,
  type RefineReportParamsDrawerRef,
  type OnSubmitRefineReportParams,
} from './components';

export const TripsTrackingPage = () => {
  const navigate = useNavigate();
  const {
    filters,
    paginationControl,
    updateFilters,
    updatePaginationControl,
    clearState,
  } = useTripsTrackingSearchParams({ persisted: true });
  const { fetchPaginatedTrips } = useFetchPaginatedTrips();
  const { createScheduledReport } = useCreateScheduledReport();

  const filtersRef = React.useRef<TripFiltersRef>(null);
  const refineReportDrawerRef = React.useRef<RefineReportParamsDrawerRef>(null);

  const someFiltersAreActive = React.useMemo(() => {
    const { sameCarrierCnpj, ...rest } = filters;
    return Object.values(rest).some(value => !!value);
  }, [filters]);

  const columns = useTripColumns();

  const tripsQuery = useQuery({
    queryKey: ['fetch-paginated-trips', filters, paginationControl],
    queryFn: () => {
      return fetchPaginatedTrips({
        filters,
        pagination: paginationControl,
      });
    },
    refetchOnWindowFocus: false,
  });

  const createReportMutation = useMutation({
    mutationKey: ['create-scheduled-trips-report'],
    mutationFn: createScheduledReport,
    onSuccess: () => {
      refineReportDrawerRef.current?.close();
      notify('success', 'Relatório requisitado com sucesso.');
      navigate(SCHEDULED_REPORTS_ROUTES.tripsTracking().url);
    },
    onError: (error: any) => {
      notify('error', error?.message);
    },
  });

  const handleFilters = React.useCallback((f: typeof filters) => {
    updateFilters(f);
    updatePaginationControl({ page: 1 });
  }, [updateFilters, updatePaginationControl]);

  const handleSort = React.useCallback((p: SortingProps) => {
    updatePaginationControl({ ...p, page: 1 });
  }, [updatePaginationControl]);

  const handleDetailsNavigation = React.useCallback((row: Row<Trip>) => {
    const tripId = row.original.id;
    navigate(TRACKING_V2_ROUTES.tripDetails(tripId).url);
  }, [navigate]);

  const handleRefineReportSubmission = React.useCallback((refineParams: OnSubmitRefineReportParams) => {
    const currentFilters = filtersRef.current!.getFormData();
    createReportMutation.mutate({
      type: 'trips',
      params: {
        ...currentFilters,
        ...refineParams,
      },
    });
  }, []);

  return (
    <Scaffold>
      <TabbedNavigation />
      <div className={'flex flex-col gap-6'}>
        <PageHeaderForFilters
          headerTitle={({ open }) => (
            <>
              <PageTitle />
              <ClearFiltersButton
                visible={!open && someFiltersAreActive}
                onClick={filtersRef.current?.resetFilters}
              />
            </>
          )}
        >
          <RefineReportParamsDrawer
            ref={refineReportDrawerRef}
            onSubmit={handleRefineReportSubmission}
            loading={createReportMutation.isLoading}
          />
          <TripFilters
            ref={filtersRef}
            onSubmitFilters={handleFilters}
            onClickRequestReport={refineReportDrawerRef.current?.open}
            onResetFilters={clearState}
          />
        </PageHeaderForFilters>
        <DataTableV2
          data={tripsQuery?.data?.data ?? []}
          columns={columns}
          manualPagination
          showControlColumns
          onPaginationChange={updatePaginationControl}
          onSort={handleSort}
          onRowClick={handleDetailsNavigation}
          isLoading={tripsQuery.isLoading}
          pagination={{
            ...tripsQuery?.data?.meta,
            currentPage: paginationControl.page,
            perPage: paginationControl.limit,
          }}
        />
      </div>
    </Scaffold>
  );
};